import { FC, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';

import { ApiCaseResponseV2 } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { Link } from '@/shared/uikit/Link';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

type CasesCellProps = {
  cases?: ApiCaseResponseV2[];
  maxWidth?: number;
  maxLines?: number;
};

const CELL_PADDING = 24;
const FONT = '14px Manrope';

export const CasesCell: FC<CasesCellProps> = ({ cases, maxWidth = 300, maxLines = 2 }) => {
  const { t } = useTranslation('features/vfo', {
    keyPrefix: 'casesCell',
  });
  const [visibleCases, setVisibleCases] = useState<ApiCaseResponseV2[]>([]);
  const [overflowCount, setOverflowCount] = useState(0);

  const router = useAppRouter();

  const availableWidth = maxWidth - CELL_PADDING;

  useLayoutEffect(() => {
    if (!cases || cases.length === 0) {
      setVisibleCases([]);
      setOverflowCount(0);
      return;
    }

    // canvas to measure the text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    context.font = FONT;

    const measure = (text: string) => context.measureText(text).width;
    const separatorWidth = measure(', ');

    const checkFit = (count: number): boolean => {
      const subset = cases.slice(0, count);
      const hasOverflow = count < cases.length;
      const overflowText = hasOverflow ? ` (+${cases.length - count})` : '';
      const overflowWidth = hasOverflow ? measure(overflowText) : 0;

      let currentLineWidth = 0;
      let currentLine = 1;

      for (let i = 0; i < subset.length; i++) {
        const item = subset[i];
        const itemWidth = measure(item.title);

        const isStartOfLine = currentLineWidth === 0;
        const widthToAdd = isStartOfLine ? itemWidth : separatorWidth + itemWidth + overflowWidth;
        if (currentLineWidth + widthToAdd <= availableWidth) {
          currentLineWidth += widthToAdd;
        } else {
          currentLine++;
          if (currentLine > maxLines) return false;
          currentLineWidth = itemWidth;

          if (currentLineWidth > availableWidth) {
            currentLineWidth = availableWidth;
          }
        }
      }

      if (hasOverflow) {
        if (currentLineWidth + overflowWidth <= availableWidth) {
          return true;
        }
        return currentLine < maxLines;
      }

      return true;
    };

    let maxCount = 1; // display at least one case
    for (let i = cases.length; i >= 1; i--) {
      if (checkFit(i)) {
        maxCount = i;
        break;
      }
    }

    setVisibleCases(cases.slice(0, maxCount));
    setOverflowCount(cases.length - maxCount);
  }, [cases, maxLines, maxWidth]);

  if (!cases || cases.length === 0) {
    return <span>{t('none')}</span>;
  }

  return (
    <Container>
      {visibleCases.map((item, i) => (
        <span key={item.id}>
          <LinkStyled
            onClick={() => {
              router.push(`/vfo/case/${item.id}`, undefined, {
                shallow: true,
              });
            }}
          >
            {item.title}
          </LinkStyled>
          {i < visibleCases.length - 1 && <span>, </span>}
        </span>
      ))}
      {overflowCount > 0 && <span>{` (+${overflowCount})`}</span>}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  word-wrap: break-word;
`;

const LinkStyled = styled(Link)`
  &&& {
    color: ${COLORS.systemSky200};
    &:hover {
      color: ${COLORS.systemSky200};
    }
  }
`;
